import React, { useState } from "react";
import { Formik } from "formik";
import Dropzone from "react-dropzone";
import * as Yup from "yup";

import { faviconRepository } from "domain/repository";

import { Segment, Button, Header, Message } from "semantic-ui-react";

type Props = {
  storeId: string;
};

const acceptFileType = ["image/jpeg", "image/png", "image/x-icon"];
const acceptFileTypeDisplay = ["ico", "jpg", "jpeg", "png"];

const initialValues = { files: [] };
const validationSchema = Yup.object().shape({
  files: Yup.array(
    Yup.mixed().test(
      "fileSize",
      "ファイルサイズが大きすぎます。",
      (value) => value && value.size <= 10 * 1024 * 1024
    )
  ).max(1, "1つのファイルを設定してください。"),
});

const FaviconUploadForm: React.FC<Props> = ({ storeId }) => {
  const [upload, setUpload] = useState(false);

  return (
    <Segment>
      <Formik
        initialValues={initialValues}
        initialStatus={{
          success: "",
          error: "",
        }}
        onSubmit={(values, actions) => {
          setUpload(true);
          faviconRepository
            .updateFavicon(storeId, values.files[0])
            .then(() => {
              actions.setStatus({
                success: "ファイルアップロードに成功しました。",
              });
              actions.setFieldValue("files", []);
            })
            .catch((e) => {
              actions.setStatus({
                error: "ファイルアップロードに失敗しました。",
              });
            })
            .finally(() => setUpload(false));
        }}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          handleSubmit,
          status,
          setFieldValue,
          setStatus,
        }) => (
          <>
            <Message>
              <Message.Header content="選択中のファイル" />
              <Message.Content>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                    {values.files.length === 0 ? (
                      <li>ファイルが選択されていません</li>
                    ) : (
                      values.files.map((file: File, i) => (
                        <li key={i}>
                          {file.name} （
                          {Math.floor((file.size / 1024 / 1024) * 100) / 100}
                          MB）
                        </li>
                      ))
                    )}
                  </ul>
                  <Button
                    content="選択解除"
                    color="red"
                    onClick={() => setFieldValue("files", [])}
                    disabled={values.files.length === 0}
                    style={{ alignSelf: "flex-end" }}
                  />
                </div>
                <p>
                  ※
                  ファビコンの更新は、反映されるまでに数日かかることがあります。
                </p>
              </Message.Content>
            </Message>
            {status.success && <Message success content={status.success} />}
            {status.error && <Message error content={status.error} />}
            {errors.files && <Message error content={errors.files} />}
            <Dropzone
              onDrop={(acceptedFiles) => {
                setFieldValue("files", acceptedFiles);
                setStatus({ success: "", error: "" });
              }}
            >
              {({ getInputProps, getRootProps }) => (
                <div {...getRootProps()}>
                  <Segment
                    placeholder
                    loading={upload}
                    style={{ marginBottom: "1rem" }}
                  >
                    <Header
                      textAlign="center"
                      content="ここにファイルをドロップ"
                      as="h5"
                      color="grey"
                    />
                    <input
                      {...getInputProps()}
                      type="file"
                      accept={acceptFileType.join(",")}
                    />
                    <Button
                      content="ファイルを選択"
                      color="blue"
                      style={{ marginBottom: "1rem" }}
                    />
                    <div style={{ textAlign: "center" }}>
                      <p>ファイルサイズ： 10MB以下</p>
                      <p>
                        ファイル形式：
                        {acceptFileTypeDisplay.join(", ")}
                      </p>
                    </div>
                  </Segment>
                </div>
              )}
            </Dropzone>
            <div style={{ textAlign: "center" }}>
              <Button
                color="blue"
                content="アップロード"
                onClick={() => handleSubmit()}
                disabled={
                  values.files.length === 0 || errors.files || upload
                    ? true
                    : false
                }
              />
            </div>
          </>
        )}
      </Formik>
    </Segment>
  );
};

export default FaviconUploadForm;
